import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, TrendingUp, Zap } from "lucide-react";
import { useLatestArticle } from "@/hooks/useArticles";
import { formatDistanceToNow } from "date-fns";

const getCategoryStyles = (category: string) => {
  switch (category?.toLowerCase()) {
    case 'daily-briefing':
      return 'bg-primary/20 text-primary border-primary/30';
    case 'breaking':
      return 'bg-destructive/20 text-destructive border-destructive/30';
    case 'market-movers':
      return 'bg-green-500/20 text-green-500 border-green-500/30';
    case 'deep-dive':
      return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    case 'market-flash':
      return 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30';
    default:
      return 'bg-muted/20 text-muted-foreground border-muted/30';
  }
};

export const JustInHero = () => {
  const { data: article, isLoading } = useLatestArticle();

  if (isLoading) {
    return (
      <section className="container mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-2 h-8 bg-destructive rounded-full animate-pulse" />
          <Skeleton className="h-8 w-32" />
        </div>
        <Skeleton className="h-[280px] rounded-xl" />
      </section>
    );
  }

  if (!article) {
    return null;
  }

  const timeAgo = article.publish_date 
    ? formatDistanceToNow(new Date(article.publish_date), { addSuffix: true })
    : 'Recently';

  return (
    <section className="container mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-2 h-8 bg-destructive rounded-full" />
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-destructive" />
          <h2 className="text-2xl font-bold text-foreground">Just In</h2>
        </div>
        <Badge variant="outline" className="gap-1 border-destructive/30 text-destructive animate-pulse">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-destructive"></span>
          </span>
          LIVE
        </Badge>
      </div>

      <Link to={`/articles/${article.slug}`} className="group block">
        <Card className="overflow-hidden border-border/50 bg-gradient-to-br from-card via-card to-destructive/5 hover:border-destructive/50 transition-all duration-300">
          <CardContent className="p-0">
            <div className="relative min-h-[280px] flex flex-col">
              {/* Background gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-destructive/10 via-transparent to-transparent" />
              
              <div className="relative z-10 p-8 flex flex-col justify-end h-full">
                <div className="flex items-center gap-3 mb-4">
                  <Badge className={`gap-1 ${getCategoryStyles(article.category)}`}>
                    {article.category?.replace('-', ' ').toUpperCase() || 'ANALYSIS'}
                  </Badge>
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {timeAgo}
                  </span>
                </div>
                
                <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-3 group-hover:text-destructive transition-colors leading-tight">
                  {article.headline}
                </h3>
                
                <p className="text-muted-foreground mb-4 line-clamp-2">
                  {article.deck || article.summary}
                </p>
                
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {article.reading_time || 5} min read
                  </span>
                  {article.opportunity_score && (
                    <Badge variant="outline" className="gap-1 border-green-500/30 text-green-500">
                      <TrendingUp className="w-3 h-3" />
                      Score: {article.opportunity_score}
                    </Badge>
                  )}
                  {article.tokens_featured && article.tokens_featured.length > 0 && (
                    <div className="flex gap-2">
                      {article.tokens_featured.slice(0, 3).map((token) => (
                        <Badge key={token} variant="secondary" className="text-xs">
                          {token}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </Link>
    </section>
  );
};
